/**
 * Search — typed Eden Treaty wrapper for `GET /api/companies/:companyId/search`.
 *
 * The backend wraps the payload in `success(...)` so the wire body is
 * `{ data: { status, hits, engine } }` on 200, and `{ error: { code, message } }`
 * on 400 (invalid channels) / 503 (engine unavailable). We surface a 503 as a
 * dedicated `SearchUnavailableError` so the page can render a distinct
 * "search unavailable" state rather than a generic error toast, and pass the
 * `status: "warming"` payload straight through so the caller can poll.
 */

import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import type { ISearchPayload, TSearchChannel } from "../types/search.ts"
import { extractErrorMessage } from "./errors.ts"

/** Thrown when the search engine is unavailable (503) — the embedding service
 * is down or the org's index can't be built. Distinct from a transient error so
 * the UI can offer a "try again" affordance with the right copy. */
export class SearchUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "SearchUnavailableError"
  }
}

export interface ISearchRequest {
  q: string
  channels?: TSearchChannel[]
  speaker?: string
  from?: string
  to?: string
  meetingId?: string
  seriesId?: string
  language?: string
  limit?: number
}

/**
 * Run a search against the active company's engine. Resolves with the payload
 * (which may carry `status: "warming"` when the engine is still warming — the
 * caller retries with backoff). Rejects with `SearchUnavailableError` on 503.
 */
export async function searchMeetings(company: CompanyApi, req: ISearchRequest): Promise<ISearchPayload> {
  const result = await company.search.get({
    query: {
      q: req.q,
      channels: req.channels && req.channels.length > 0 ? req.channels.join(",") : undefined,
      speaker: req.speaker,
      from: req.from,
      to: req.to,
      meetingId: req.meetingId,
      seriesId: req.seriesId,
      language: req.language,
      limit: req.limit,
    },
  })

  if (result.error) {
    // Eden types the error status as the validation default (422) since the
    // route declares only a 200 schema; at runtime it carries the real HTTP
    // status the handler sets (400 invalid channels / 503 unavailable). Widen
    // to `number` so the runtime 503 check isn't rejected as an impossible
    // literal comparison.
    const httpStatus: number = result.error.status
    if (httpStatus === 503) {
      throw new SearchUnavailableError(extractErrorMessage(result.error))
    }
    throw new Error(extractErrorMessage(result.error))
  }

  const body = result.data
  if (body && "data" in body) {
    return body.data
  }

  // 200 with an error envelope — shouldn't happen (errors carry a non-2xx
  // status), but keep the type union honest rather than returning junk.
  throw new Error(extractErrorMessage(body))
}
